import apiReq from 'core/api';

// ─── Типы (по схеме раздела Audit свагера ARM API) ──────────────────────────────

export type AuditMethod = 'POST' | 'PATCH' | 'DELETE';

/** Параметры выборки журнала действий (`GET /user-actions`). */
export interface AuditFilters {
  user_id?: string;
  project_id?: string;
  category?: string;
  action?: string;
  http_method?: AuditMethod;
  subject_type?: string;
  subject_id?: string;
  /** Unix-секунды */
  date_from?: number;
  /** Unix-секунды */
  date_to?: number;
  search?: string;
  limit?: number;
  offset?: number;
}

/** Запись журнала действий пользователя. */
export interface UserActionLogItem {
  id: string;
  user_id: string;
  user_email: string | null;
  project_id: string | null;
  project_name: string | null;
  category: string;
  action: string;
  http_method: string;
  url: string;
  payload: Record<string, unknown> | null;
  /** Unix-секунды */
  created_at: number;
}

export interface UserActionLogResponse {
  count: number;
  items: UserActionLogItem[];
}

/**
 * Собирает query-строку из фильтров журнала.
 * Пустые строки, undefined/null и NaN пропускаются; `0` (валидный offset) сохраняется.
 * Возвращает строку с ведущим `?` либо пустую строку.
 */
export function buildAuditQuery(filters: AuditFilters): string {
  const params = new URLSearchParams();

  const add = (key: string, value: unknown): void => {
    if (value === undefined || value === null) return;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return;
      params.append(key, trimmed);
      return;
    }
    if (typeof value === 'number') {
      if (Number.isNaN(value)) return;
      params.append(key, String(value));
    }
  };

  add('user_id', filters.user_id);
  add('project_id', filters.project_id);
  add('category', filters.category);
  add('action', filters.action);
  add('http_method', filters.http_method);
  add('subject_type', filters.subject_type);
  add('subject_id', filters.subject_id);
  add('date_from', filters.date_from);
  add('date_to', filters.date_to);
  add('search', filters.search);
  add('limit', filters.limit);
  add('offset', filters.offset);

  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// ─── Запросы ────────────────────────────────────────────────────────────────────

/** `GET /user-actions` — журнал действий пользователей. */
export const listUserActions = (
  filters: AuditFilters = {}
): Promise<UserActionLogResponse | undefined> =>
  apiReq<UserActionLogResponse>(`user-actions${buildAuditQuery(filters)}`);

/** `GET /user-actions/categories` — список категорий журнала. */
export const getAuditCategories = (): Promise<string[] | undefined> =>
  apiReq<string[]>('user-actions/categories');

/** `GET /user-actions/actions` — список действий (опц. в рамках категории). */
export const getAuditActions = (category?: string): Promise<string[] | undefined> => {
  const query = category ? `?category=${encodeURIComponent(category)}` : '';
  return apiReq<string[]>(`user-actions/actions${query}`);
};
